'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import type { Heading } from '@/lib/support/markdown'

const HEADER_OFFSET = 96
const SCROLL_UNLOCK_TIMEOUT = 1200

interface SupportTocProps {
  headings: Heading[]
}

export default function SupportToc ({ headings }: SupportTocProps) {
  const t = useTranslations('Support')
  const [activeId, setActiveId] = useState<string | null>(null)
  const lockRef = useRef(false)
  const unlockTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const releaseLock = useCallback(() => {
    lockRef.current = false
    if (unlockTimer.current) {
      clearTimeout(unlockTimer.current)
      unlockTimer.current = null
    }
    window.removeEventListener('scrollend', releaseLock)
  }, [])

  useEffect(() => {
    if (headings.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (lockRef.current) return
        const visible = entries.filter((entry) => entry.isIntersecting)
        if (visible.length > 0) {
          setActiveId(visible[visible.length - 1].target.id)
        }
      },
      { rootMargin: '-80px 0px -65% 0px', threshold: 0 }
    )

    for (const heading of headings) {
      const element = document.getElementById(heading.id)
      if (element) observer.observe(element)
    }

    return () => observer.disconnect()
  }, [headings])

  useEffect(() => {
    return () => releaseLock()
  }, [releaseLock])

  if (headings.length < 2) return null

  const scrollTo = (id: string) => {
    const element = document.getElementById(id)
    if (!element) return

    lockRef.current = true
    setActiveId(id)

    if (unlockTimer.current) clearTimeout(unlockTimer.current)
    window.addEventListener('scrollend', releaseLock, { once: true })
    unlockTimer.current = setTimeout(releaseLock, SCROLL_UNLOCK_TIMEOUT)

    const top = element.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET
    window.scrollTo({ top, behavior: 'smooth' })
  }

  return (
    <nav className='support-toc' aria-label={t('onThisPage')}>
      <h2>{t('onThisPage')}</h2>
      <ul>
        {headings.map((heading) => (
          <li key={heading.id} className={heading.level === 3 ? 'is-sub' : undefined}>
            <a
              href={`#${heading.id}`}
              className={activeId === heading.id ? 'is-active' : undefined}
              onClick={(event) => {
                event.preventDefault()
                scrollTo(heading.id)
              }}
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}
